import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Upload, Save, Eye, FileText, Phone, Link, Image } from "lucide-react";
import { RichTextEditor } from "@/components/ui/rich-text-editor";

// ContentManagement component - Updated to use RichTextEditor for Arabic support

interface SiteContent {
  id: string;
  content_type: string;
  title: string | null;
  content: any;
  image_url: string | null;
  is_active: boolean;
}

const ContentManagement = () => {
  const [content, setContent] = useState<{ [key: string]: SiteContent }>({});
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const { data, error } = await supabase
        .from('site_content')
        .select('*');
      
      if (error) throw error;

      const contentMap: { [key: string]: SiteContent } = {};
      data?.forEach(item => {
        contentMap[item.content_type] = item;
      });
      
      setContent(contentMap);
    } catch (error) {
      console.error('Error fetching content:', error);
      toast({
        title: "خطأ",
        description: "فشل في تحميل المحتوى",
        variant: "destructive",
      });
    }
  };

  const updateContent = async (contentType: string, updates: Partial<SiteContent>) => {
    setLoading(true);
    try {
      const existingContent = content[contentType];
      const { error } = await supabase
        .from('site_content')
        .upsert({
          id: existingContent?.id,
          content_type: contentType,
          ...updates,
        });

      if (error) throw error;

      await fetchContent();
      toast({
        title: "تم الحفظ",
        description: "تم حفظ التغييرات بنجاح",
      });
    } catch (error) {
      console.error('Error updating content:', error);
      toast({
        title: "خطأ",
        description: "فشل في حفظ التغييرات",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleContactInfoUpdate = () => {
    const contactInfo = content['contact_info']?.content || {};
    updateContent('contact_info', {
      title: 'معلومات التواصل',
      content: contactInfo,
    });
  };

  const handleFooterLinksUpdate = () => {
    const footerLinks = content['footer_links']?.content || { links: [] };
    updateContent('footer_links', {
      title: 'روابط الفوتر',
      content: footerLinks,
    });
  };

  const handleTermsUpdate = () => {
    const terms = content['terms']?.content || {};
    updateContent('terms', {
      title: 'الشروط والأحكام',
      content: terms,
    });
  };

  const handlePrivacyUpdate = () => {
    const privacy = content['privacy']?.content || {};
    updateContent('privacy', {
      title: 'سياسة الخصوصية',
      content: privacy,
    });
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // التحقق من نوع الملف (دعم الصور بما في ذلك SVG)
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "خطأ",
        description: "يجب اختيار ملف صورة صالح (JPG, PNG, GIF, WebP, SVG)",
        variant: "destructive",
      });
      return;
    }

    // التحقق من حجم الملف (2MB كحد أقصى)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: "خطأ",
        description: "حجم الملف يجب أن يكون أقل من 2 ميجابايت",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    try {
      // إنشاء اسم ملف فريد
      const fileExt = file.name.split('.').pop();
      const fileName = `logo-${Date.now()}.${fileExt}`;

      // رفع الملف إلى Supabase Storage
      const { data, error } = await supabase.storage
        .from('logos')
        .upload(fileName, file);

      if (error) throw error;

      // الحصول على URL العام للملف
      const { data: { publicUrl } } = supabase.storage
        .from('logos')
        .getPublicUrl(fileName);

      // تحديث المحتوى بالرابط الجديد
      updateNestedContent('ministry_logo', ['url'], publicUrl);

      toast({
        title: "تم الرفع",
        description: "تم رفع الشعار بنجاح",
      });
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast({
        title: "خطأ",
        description: "فشل في رفع الشعار",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSiteLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // التحقق من نوع الملف (دعم الصور بما في ذلك SVG)
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "خطأ",
        description: "يجب اختيار ملف صورة صالح (JPG, PNG, GIF, WebP, SVG)",
        variant: "destructive",
      });
      return;
    }

    // التحقق من حجم الملف (2MB كحد أقصى)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: "خطأ",
        description: "حجم الملف يجب أن يكون أقل من 2 ميجابايت",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    try {
      // إنشاء اسم ملف فريد
      const fileExt = file.name.split('.').pop();
      const fileName = `site-logo-${Date.now()}.${fileExt}`;

      // رفع الملف إلى Supabase Storage
      const { data, error } = await supabase.storage
        .from('logos')
        .upload(fileName, file);

      if (error) throw error;

      // الحصول على URL العام للملف
      const { data: { publicUrl } } = supabase.storage
        .from('logos')
        .getPublicUrl(fileName);

      // تحديث المحتوى بالرابط الجديد
      updateNestedContent('site_logo', ['url'], publicUrl);

      toast({
        title: "تم الرفع",
        description: "تم رفع شعار الموقع بنجاح",
      });
    } catch (error) {
      console.error('Error uploading site logo:', error);
      toast({
        title: "خطأ",
        description: "فشل في رفع شعار الموقع",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSiteLogoUpdate = () => {
    const logoData = content['site_logo']?.content || {};
    updateContent('site_logo', {
      title: 'شعار الموقع',
      content: logoData,
      image_url: logoData.url || null,
    });
  };

  const handleSiteLogoDelete = async () => {
    try {
      setLoading(true);
      
      // حذف الملف من التخزين إذا كان موجود
      const currentUrl = content['site_logo']?.content?.url;
      if (currentUrl && currentUrl.includes('logos/')) {
        const fileName = currentUrl.split('/').pop();
        if (fileName) {
          await supabase.storage
            .from('logos')
            .remove([fileName]);
        }
      }

      // حذف المحتوى من قاعدة البيانات
      const { error } = await supabase
        .from('site_content')
        .delete()
        .eq('content_type', 'site_logo');

      if (error) throw error;

      await fetchContent();
      toast({
        title: "تم الحذف",
        description: "تم حذف شعار الموقع بنجاح",
      });
    } catch (error) {
      console.error('Error deleting site logo:', error);
      toast({
        title: "خطأ",
        description: "فشل في حذف شعار الموقع",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMinistryLogoUpdate = () => {
    const logoData = content['ministry_logo']?.content || {};
    updateContent('ministry_logo', {
      title: 'شعار الوزارة',
      content: logoData,
      image_url: logoData.url || null,
    });
  };

  const updateNestedContent = (contentType: string, path: string[], value: any) => {
    const updated = { ...content };
    if (!updated[contentType]) {
      updated[contentType] = { content: {} } as SiteContent;
    }
    
    let current = updated[contentType].content;
    for (let i = 0; i < path.length - 1; i++) {
      if (!current[path[i]]) {
        current[path[i]] = {};
      }
      current = current[path[i]];
    }
    current[path[path.length - 1]] = value;
    setContent(updated);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200" dir="rtl">
      {/* عنوان إدارة المحتوى */}
      <div className="bg-gradient-to-r from-school-navy/90 to-school-blue/90 rounded-t-xl p-6">
        <h2 className="text-2xl font-bold text-white text-right">إدارة المحتوى</h2>
      </div>
      
      {/* محتوى البطاقة */}
      <div className="p-6">
        <Tabs defaultValue="site-logo" className="w-full">
          <TabsList className="grid w-full grid-cols-6 mb-6">
            <TabsTrigger value="site-logo" className="flex items-center gap-2 flex-row-reverse">
              <Image className="h-4 w-4" />
              شعار الموقع
            </TabsTrigger>
            <TabsTrigger value="logo" className="flex items-center gap-2 flex-row-reverse">
              <Image className="h-4 w-4" />
              شعار الوزارة
            </TabsTrigger>
            <TabsTrigger value="contact" className="flex items-center gap-2 flex-row-reverse">
              <Phone className="h-4 w-4" />
              التواصل
            </TabsTrigger>
            <TabsTrigger value="footer" className="flex items-center gap-2 flex-row-reverse">
              <Link className="h-4 w-4" />
              الفوتر
            </TabsTrigger>
            <TabsTrigger value="terms" className="flex items-center gap-2 flex-row-reverse">
              <FileText className="h-4 w-4" />
              الشروط
            </TabsTrigger>
            <TabsTrigger value="privacy" className="flex items-center gap-2 flex-row-reverse">
              <Eye className="h-4 w-4" />
              الخصوصية
            </TabsTrigger>
          </TabsList>

        {/* شعار الموقع */}
        <TabsContent value="site-logo">
          <Card className="text-right">
            <CardHeader className="rounded-t-lg" style={{backgroundColor: '#C2C1C1'}}>
              <CardTitle className="text-right text-gray-800">شعار الموقع</CardTitle>
              <CardDescription className="text-right text-gray-600">إدارة شعار الموقع العام لجميع المدارس</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-right mt-4 pt-6">
              <div>
                <Label htmlFor="site-logo-upload" className="text-right">تحميل شعار جديد</Label>
                <p className="text-xs text-gray-500 mb-2 text-right">الصيغ المدعومة: JPG, PNG, GIF, WebP, SVG (حد أقصى: 2MB)</p>
                <div className="flex items-center gap-2">
                  <Input
                    id="site-logo-upload"
                    type="file"
                    accept=".jpg,.jpeg,.png,.gif,.webp,.svg,image/*"
                    onChange={handleSiteLogoUpload}
                    disabled={uploading}
                    className="text-right"
                  />
                  <Button 
                    variant="outline" 
                    disabled={uploading}
                    onClick={() => document.getElementById('site-logo-upload')?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? 'جاري الرفع...' : 'اختر ملف'}
                  </Button>
                </div>
              </div>
              
              <Separator />
              
              <div>
                <Label htmlFor="site-logo-url" className="text-right">أو أدخل رابط الشعار</Label>
                <Input
                  id="site-logo-url"
                  className="text-right"
                  value={content['site_logo']?.content?.url || ''}
                  onChange={(e) => updateNestedContent('site_logo', ['url'], e.target.value)}
                  placeholder="https://example.com/site-logo.png"
                />
              </div>
              
              {content['site_logo']?.content?.url && (
                <div>
                  <Label className="text-right">معاينة الشعار الحالي</Label>
                  <div className="mt-2 p-4 border rounded-lg bg-gray-50 flex justify-center">
                    <img 
                      src={content['site_logo']?.content?.url} 
                      alt="معاينة شعار الموقع" 
                      className="h-20 object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              )}
              
              <div>
                <Label htmlFor="site-logo-alt" className="text-right">النص البديل</Label>
                <Input
                  id="site-logo-alt"
                  className="text-right"
                  value={content['site_logo']?.content?.alt_text || ''}
                  onChange={(e) => updateNestedContent('site_logo', ['alt_text'], e.target.value)}
                  placeholder="شعار الموقع"
                />
              </div>
              
              <div className="flex gap-2 justify-start">
                <Button onClick={handleSiteLogoUpdate} disabled={loading}>
                  <Save className="h-4 w-4 mr-2" />
                  حفظ الشعار
                </Button>
                {content['site_logo']?.content?.url && (
                  <Button 
                    onClick={handleSiteLogoDelete} 
                    disabled={loading} 
                    variant="destructive"
                  >
                    حذف الشعار
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* شعار الوزارة */}
        <TabsContent value="logo">
          <Card className="text-right">
            <CardHeader className="rounded-t-lg" style={{backgroundColor: '#C2C1C1'}}>
              <CardTitle className="text-right text-gray-800">شعار الوزارة</CardTitle>
              <CardDescription className="text-right text-gray-600">إدارة شعار وزارة التعليم</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-right mt-4 pt-6">
              <div>
                <Label htmlFor="logo-upload" className="text-right">تحميل شعار جديد</Label>
                <p className="text-xs text-gray-500 mb-2 text-right">الصيغ المدعومة: JPG, PNG, GIF, WebP, SVG (حد أقصى: 2MB)</p>
                <div className="flex items-center gap-2">
                  <Input
                    id="logo-upload"
                    type="file"
                     accept=".jpg,.jpeg,.png,.gif,.webp,.svg,image/*"
                    onChange={handleLogoUpload}
                    disabled={uploading}
                    className="text-right"
                  />
                  <Button 
                    variant="outline" 
                    disabled={uploading}
                    onClick={() => document.getElementById('logo-upload')?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? 'جاري الرفع...' : 'اختر ملف'}
                  </Button>
                </div>
              </div>
              
              <Separator />
              
              <div>
                <Label htmlFor="logo-url" className="text-right">أو أدخل رابط الشعار</Label>
                <Input
                  id="logo-url"
                  className="text-right"
                  value={content['ministry_logo']?.content?.url || ''}
                  onChange={(e) => updateNestedContent('ministry_logo', ['url'], e.target.value)}
                  placeholder="https://example.com/logo.png"
                />
              </div>
              
              {content['ministry_logo']?.content?.url && (
                <div>
                  <Label className="text-right">معاينة الشعار الحالي</Label>
                  <div className="mt-2 p-4 border rounded-lg bg-gray-50">
                    <img 
                      src={content['ministry_logo']?.content?.url} 
                      alt="معاينة الشعار" 
                      className="h-20 object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              )}
              
              <div>
                <Label htmlFor="logo-alt" className="text-right">النص البديل</Label>
                <Input
                  id="logo-alt"
                  className="text-right"
                  value={content['ministry_logo']?.content?.alt_text || ''}
                  onChange={(e) => updateNestedContent('ministry_logo', ['alt_text'], e.target.value)}
                  placeholder="شعار وزارة التعليم"
                />
              </div>
              <Button onClick={handleMinistryLogoUpdate} disabled={loading} className="mr-auto">
                <Save className="h-4 w-4 mr-2" />
                حفظ الشعار
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* معلومات التواصل */}
        <TabsContent value="contact">
          <Card className="text-right">
            <CardHeader className="rounded-t-lg" style={{backgroundColor: '#C2C1C1'}}>
              <CardTitle className="text-right text-gray-800">معلومات التواصل</CardTitle>
              <CardDescription className="text-right text-gray-600">إدارة وسائل التواصل ومعلومات الاتصال</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-right mt-4 pt-6">
              <div>
                <Label htmlFor="phone" className="text-right">رقم الهاتف</Label>
                <Input
                  id="phone"
                  className="text-right"
                  value={content['contact_info']?.content?.phone || ''}
                  onChange={(e) => updateNestedContent('contact_info', ['phone'], e.target.value)}
                  placeholder="+966 11 4567890"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-right">البريد الإلكتروني</Label>
                <Input
                  id="email"
                  type="email"
                  className="text-right"
                  value={content['contact_info']?.content?.email || ''}
                  onChange={(e) => updateNestedContent('contact_info', ['email'], e.target.value)}
                  placeholder="info@education.gov.sa"
                />
              </div>
              <div>
                <Label htmlFor="address" className="text-right">العنوان</Label>
                <Input
                  id="address"
                  className="text-right"
                  value={content['contact_info']?.content?.address || ''}
                  onChange={(e) => updateNestedContent('contact_info', ['address'], e.target.value)}
                  placeholder="الرياض، المملكة العربية السعودية"
                />
              </div>
              <div>
                <Label htmlFor="working-hours" className="text-right">ساعات العمل</Label>
                <Input
                  id="working-hours"
                  className="text-right"
                  value={content['contact_info']?.content?.working_hours || ''}
                  onChange={(e) => updateNestedContent('contact_info', ['working_hours'], e.target.value)}
                  placeholder="الأحد - الخميس: 7:30 ص - 3:30 م"
                />
              </div>
              <Button onClick={handleContactInfoUpdate} disabled={loading} className="mr-auto">
                <Save className="h-4 w-4 mr-2" />
                حفظ معلومات التواصل
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* روابط الفوتر */}
        <TabsContent value="footer">
          <Card className="text-right">
            <CardHeader className="rounded-t-lg" style={{backgroundColor: '#C2C1C1'}}>
              <CardTitle className="text-right text-gray-800">روابط الفوتر</CardTitle>
              <CardDescription className="text-right text-gray-600">إدارة الروابط في تذييل الموقع</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-right mt-4 pt-6">
              <div className="space-y-2">
                <Label className="text-right">الروابط الحالية</Label>
                {content['footer_links']?.content?.links?.map((link: any, index: number) => (
                  <div key={index} className="grid grid-cols-2 gap-2 p-4 border rounded">
                    <Input
                      placeholder="عنوان الرابط"
                      className="text-right"
                      value={link.title}
                      onChange={(e) => {
                        const updated = { ...content };
                        updated['footer_links'].content.links[index].title = e.target.value;
                        setContent(updated);
                      }}
                    />
                    <Input
                      placeholder="الرابط"
                      className="text-right"
                      value={link.url}
                      onChange={(e) => {
                        const updated = { ...content };
                        updated['footer_links'].content.links[index].url = e.target.value;
                        setContent(updated);
                      }}
                    />
                  </div>
                ))}
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  const updated = { ...content };
                  if (!updated['footer_links']) {
                    updated['footer_links'] = { content: { links: [] } } as SiteContent;
                  }
                  if (!updated['footer_links'].content.links) {
                    updated['footer_links'].content.links = [];
                  }
                  updated['footer_links'].content.links.push({ title: '', url: '', external: false });
                  setContent(updated);
                }}
              >
                إضافة رابط جديد
              </Button>
              <Button onClick={handleFooterLinksUpdate} disabled={loading} className="mr-auto">
                <Save className="h-4 w-4 mr-2" />
                حفظ روابط الفوتر
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* الشروط والأحكام */}
        <TabsContent value="terms">
          <Card className="text-right">
            <CardHeader className="rounded-t-lg" style={{backgroundColor: '#C2C1C1'}}>
              <CardTitle className="text-right text-gray-800">الشروط والأحكام</CardTitle>
              <CardDescription className="text-right text-gray-600">محتوى صفحة الشروط والأحكام - استخدم الأدوات لتنسيق النص والألوان</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-right mt-4 pt-6">
               <div>
                 <Label htmlFor="terms-content" className="text-right">المحتوى</Label>
                 <div className="mt-2">
                   <RichTextEditor
                     value={content['terms']?.content?.content || ''}
                     onChange={(value) => updateNestedContent('terms', ['content'], value)}
                     placeholder="اكتب محتوى الشروط والأحكام هنا..."
                     dir="rtl"
                     className="min-h-[300px]"
                   />
                 </div>
               </div>
              <Button onClick={handleTermsUpdate} disabled={loading} className="mr-auto">
                <Save className="h-4 w-4 mr-2" />
                حفظ الشروط والأحكام
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* سياسة الخصوصية */}
        <TabsContent value="privacy">
          <Card className="text-right">
            <CardHeader className="rounded-t-lg" style={{backgroundColor: '#C2C1C1'}}>
              <CardTitle className="text-right text-gray-800">سياسة الخصوصية</CardTitle>
              <CardDescription className="text-right text-gray-600 text-xs">محتوى صفحة سياسة الخصوصية - استخدم الأدوات لتنسيق النص والألوان</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-right mt-4 pt-6">
               <div>
                 <Label htmlFor="privacy-content" className="text-right">المحتوى</Label>
                 <div className="mt-2">
                   <RichTextEditor
                     value={content['privacy']?.content?.content || ''}
                     onChange={(value) => updateNestedContent('privacy', ['content'], value)}
                     placeholder="اكتب محتوى سياسة الخصوصية هنا..."
                     dir="rtl"
                     className="min-h-[300px]"
                   />
                 </div>
               </div>
              <Button onClick={handlePrivacyUpdate} disabled={loading} className="mr-auto">
                <Save className="h-4 w-4 mr-2" />
                حفظ سياسة الخصوصية
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ContentManagement;